import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../components/button";
import FitnessToolsResultCard from "../../../components/cards/FitnessToolsResultCard";
import Footer from "../../../components/footer";
import Header from "../../../components/header";
import Input from "../../../components/input";
import Loader from "../../../components/loader";
import OtherFitnessTestSection from "../../../components/OtherFitnessTestSection";
import AISkeleton from "../../../components/skeleton/AISkeleton";
import { useTranslate } from "../../../context/TranslationContext";
import useChartPeriod from "../../../hooks/useChartPeriod";
import {
  useCreateBalanceMutation,
  useGetBalanceChartQuery,
  useGetBalanceQuery,
  useUpdateBalanceMutation,
} from "../../../redux/Apis/FitnessTest/Balance";
import { useCreateAISuggestionMutation } from "../../../redux/Apis/TrackLecture";
import { useStaticData } from "../../../utils/data";
import { formatAISuggestion, setMetaTags } from "../../../utils/helper";

const BalanceTest = () => {
  const { t } = useTranslate("balance");
  const location = useLocation();
  const { FITNESSTEST, CALCULATOR_DATA } = useStaticData();
  const lang = useSelector((state) => state.language.lang);
  const { control, getValues, watch } = useForm({
    defaultValues: {
      method: "singleleggedstand",
      gender: "male",
    },
  });
  const method = watch("method");
  const [result, setResult] = useState(null);
  const [rawInput, setRawInput] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authSlice);
  const { period, dateRange, handlePeriodChange } = useChartPeriod();
  const { startDate, endDate } = dateRange;
  const [selectedBalence, setSelectedBalence] = useState(
    CALCULATOR_DATA.BALENCE[0].value,
  );
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [highlightAI, setHighlightAI] = useState(false);
  const aiRef = useRef(null);
  const [createAISuggestion] = useCreateAISuggestionMutation();
  const [createBalance] = useCreateBalanceMutation();
  const [updateBalance] = useUpdateBalanceMutation();
  const {
    data: balanceHistoryData,
    refetch: refetchBalance,
    isLoading,
  } = useGetBalanceQuery({ lang }, { skip: !user });
  const {
    data: balenceData,
    refetch: refetchBalenceData,
    isLoading: loadingBalence,
  } = useGetBalanceChartQuery(
    { lang, method: selectedBalence, startdate: startDate, enddate: endDate },
    { skip: !startDate || !endDate || !user },
  );
  const courseState = location?.state || {};
  // const templateId = courseState?.templateId;
  const courseId = courseState?.course;
  const sectionId = courseState?.sectionId || courseState?.section;
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (aiLoading) {
      const timer = setTimeout(() => setShowSkeleton(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowSkeleton(false);
    }
  }, [aiLoading]);

  useEffect(() => {
    if ((aiLoading || aiSuggestion) && aiRef.current) {
      setTimeout(() => {
        const y =
          aiRef.current.getBoundingClientRect().top + window.pageYOffset - 110; // header height offset

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });

        setHighlightAI(true);
        setTimeout(() => setHighlightAI(false), 3000);
      }, 300);
    }
  }, [aiLoading, aiSuggestion]);

  useEffect(() => {
    if (user) {
      refetchBalance();
    }
  }, [user, refetchBalance]);

  useEffect(() => {
    if (startDate && endDate && selectedBalence && user) {
      refetchBalenceData();
    }
  }, [startDate, endDate, selectedBalence, user, refetchBalenceData]);

  useEffect(() => {
    setMetaTags(
      "Traineego | Buy or Create Automated Courses & Tracks for Fitness & Wellness",
      "Create, manage, and sell your own courses and tracks, or buy high-quality content. Empower your career through automation and passive income today!",
    );
  }, []);

  useEffect(() => {
    if (balanceHistoryData) {
      setHistoryData(balanceHistoryData.data || []);
    }
  }, [balanceHistoryData]);

  const onCalculate = () => {
    const { method, gender, age, legTime, steps } = getValues();

    if (!gender || !age) {
      toast.warn(t("toasts.enterAgeGender"));
      return;
    }

    if (method === "singleleggedstand") {
      if (!legTime || legTime <= 0) {
        toast.warn(t("toasts.enterLegTime"));
        return;
      }
      const adjustedCategory = getSingleLegCategory(
        gender,
        parseInt(age),
        parseFloat(legTime),
      );
      setResult(adjustedCategory);
      setRawInput(`${legTime} seconds`);
    } else if (method === "tandemwalk") {
      if (!steps || steps <= 0) {
        toast.warn(t("toasts.enterSteps"));
        return;
      }
      const adjustedCategory = getTandemWalkCategory(
        gender,
        parseInt(age),
        parseInt(steps),
      );
      setResult(adjustedCategory);
      setRawInput(`${steps} steps`);
    }

    toast.success(t("toasts.calculatedSuccess"));
  };

  const getAgeAdjustment = (age, isTime = true) => {
    if (age >= 70) return isTime ? 20 : 5;
    if (age >= 60) return isTime ? 15 : 4;
    if (age >= 50) return isTime ? 12 : 3;
    if (age >= 40) return isTime ? 10 : 2;
    if (age >= 30) return isTime ? 5 : 1;
    return 0;
  };

  const getSingleLegCategory = (gender, age, rawTime) => {
    const adjusted = rawTime + getAgeAdjustment(age, true);
    const thresholds =
      gender === "female" ? [10, 20, 30, 40] : [15, 25, 35, 45];
    if (adjusted < thresholds[0]) return t("riskLevels.poor");
    if (adjusted < thresholds[1]) return t("riskLevels.belowaverage");
    if (adjusted < thresholds[2]) return t("riskLevels.average");
    if (adjusted < thresholds[3]) return t("riskLevels.good");
    return t("riskLevels.excellent");
  };

  const getTandemWalkCategory = (gender, age, rawSteps) => {
    const adjusted = rawSteps + getAgeAdjustment(age, false);
    const thresholds = gender === "female" ? [2, 4, 6, 8] : [3, 5, 7, 9];
    if (adjusted <= thresholds[0]) return t("riskLevels.poor");
    if (adjusted <= thresholds[1]) return t("riskLevels.belowaverage");
    if (adjusted <= thresholds[2]) return t("riskLevels.average");
    if (adjusted <= thresholds[3]) return t("riskLevels.good");
    return t("riskLevels.excellent");
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t("history.notCalculated");
    return new Date(timestamp).toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatHistoryEntry = (entry) => {
    const createdAt = new Date(entry.createdAt).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return (
      <div className="d-flex justify-content-between align-items-center">
        <p className="mb-0">{createdAt}</p>
        <p className="mb-0 fw-bold">{entry.result}</p>
      </div>
    );
  };

  const generateAICoach = async (inputResult) => {
    try {
      // if (!courseId || !sectionId) return;

      setAiLoading(true);

      const resultObj = {
        method: inputResult.method,
        age: inputResult.age,
        gender: inputResult.gender,
        result: `${inputResult.result} ${inputResult.unit || ""}`.trim(),
      };

      if (inputResult.timeononeleg != null && inputResult.timeononeleg !== "") {
        resultObj.timeononeleg = `${inputResult.timeononeleg} seconds`;
      }

      if (
        inputResult.stepswithoutdeviation != null &&
        inputResult.stepswithoutdeviation !== ""
      ) {
        resultObj.stepswithoutdeviation = `${inputResult.stepswithoutdeviation} steps`;
      }

      const aiPayload = {
        courseid: courseId,
        sectionid: sectionId,
        learnerid: user._id,
        type: "Balance Test",
        result: [resultObj],
      };

      const res = await createAISuggestion({
        payload: aiPayload,
        params: { lang },
      }).unwrap();

      const suggestion = res?.data?.suggestion;
      if (suggestion) setAiSuggestion(suggestion);
    } catch (err) {
      console.log("AI error", err);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveResult = async (type, resultObj) => {
    if (!resultObj.result) {
      toast.warn(t("toasts.calculateFirst"));
      return;
    }

    if (!user) {
      toast.warn(t("toasts.loginRequired"));
      // navigate("/auth/login");
      return;
    }

    try {
      const { method, legTime, steps, age, gender } = getValues();
      const payload = {
        method,
        gender,
        age,
        result: resultObj.result,
      };
      if (courseId) payload.course = courseId;
      if (method === "singleleggedstand") payload.timeononeleg = legTime;
      if (method === "tandemwalk") payload.stepswithoutdeviation = steps;

      const response = await createBalance({
        payload,
        params: { lang },
      }).unwrap();
      toast.success(response.message || t("toasts.saveSuccess"));
      refetchBalance();
      refetchBalenceData();
      // if (courseId && sectionId && templateId) {
      await generateAICoach(payload);
      // }
    } catch (error) {
      toast.error(t("toasts.saveFail"));
    }
  };

  const handleDeleteLast = async () => {
    try {
      const latestEntry = [...historyData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      )[0];
      if (latestEntry) {
        await updateBalance({
          id: latestEntry._id,
          isactive: false,
          params: { lang },
        }).unwrap();
        toast.success(t("toasts.deleteSuccess"));
        refetchBalance();
        refetchBalenceData();
      }
    } catch (error) {
      toast.error(t("toasts.deleteFail"));
    }
  };

  const toggleHistory = () => setShowHistory((prev) => !prev);

  if (isLoading || loadingBalence) {
    return <Loader loading={true} />;
  }

  const handleBalenceTabChangeFactory = (setSelectedBalence) => {
    return (index) => {
      const selected = CALCULATOR_DATA.BALENCE[index].value;
      setSelectedBalence(selected);
    };
  };

  const handleBalenceTabChange =
    handleBalenceTabChangeFactory(setSelectedBalence);

  return (
    <>
      <Header />
      <section className="banner" style={{ textAlign: "left" }}>
        <div className="container py-5">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="text-uppercase">{t("title")}</h1>
              <p className="mb-0 p-lg l-grey-text">{t("subtitle")}</p>
            </div>
          </div>

          <div className="row d-flex">
            <div
              className="col-md-12 mt-4 p-5"
              style={{ backgroundColor: "#161617", borderRadius: "2rem" }}
            >
              <div className="row">
                <div className="col-lg-8">
                  <h4 className="text-uppercase mb-4">
                    {t("calculateSectionTitle")}
                  </h4>

                  <div className="row">
                    <div className="col-md-6">
                      <Controller
                        name="gender"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <Input
                            label={t("inputs.gender")}
                            type="select"
                            options={CALCULATOR_DATA.GENDER}
                            {...field}
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6">
                      <Controller
                        name="age"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <Input
                            label={t("inputs.age")}
                            type="number"
                            placeholder={t("inputs.agePlaceholder")}
                            {...field}
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="row mt-3">
                    <div className="col-md-6">
                      <Controller
                        name="method"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label={t("inputs.method")}
                            type="select"
                            options={[
                              {
                                value: "singleleggedstand",
                                label: t(
                                  "inputs.methodOptions.singleleggedstand",
                                ),
                              },
                              {
                                value: "tandemwalk",
                                label: t("inputs.methodOptions.tandemwalk"),
                              },
                            ]}
                            {...field}
                          />
                        )}
                      />
                    </div>

                    {method === "singleleggedstand" && (
                      <div className="col-md-6">
                        <Controller
                          name="legTime"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label={t("inputs.legTime")}
                              type="number"
                              placeholder={t("inputs.legTimePlaceholder")}
                              {...field}
                            />
                          )}
                        />
                      </div>
                    )}

                    {method === "tandemwalk" && (
                      <div className="col-md-6">
                        <Controller
                          name="steps"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label={t("inputs.steps")}
                              type="number"
                              placeholder={t("inputs.stepsPlaceholder")}
                              {...field}
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>

                  <div className="row mt-5">
                    <div
                      className="col-12 bg-white py-2 px-4 d-flex justify-content-between align-items-center"
                      style={{ borderRadius: "0.5rem" }}
                    >
                      <p className="grey-text mb-0">
                        {t("calculateBox.label")}
                      </p>
                      <Button
                        text={t("calculateBox.button")}
                        type="button"
                        onClick={onCalculate}
                      />
                    </div>
                  </div>
                  {result && location.state?.returnTo && (
                    <div className="text-center mt-3">
                      <Button
                        text={t("continueLearning")}
                        className="site-btn border-btn"
                        style={{ width: "100%", maxWidth: "200px" }}
                        onClick={() =>
                          navigate(location.state.returnTo, {
                            state: {
                              ...location.state,
                              resultKey: `${location.state.sectionIndex}-${location.state.lectureIndex}-BALANCE`,
                              resultData: {
                                type: "BALANCE",
                                result: result,
                                value: rawInput,
                                timestamp: new Date().toISOString(),
                              },
                            },
                          })
                        }
                      />
                    </div>
                  )}
                </div>

                <div className="col-12 col-lg-4 mt-4 mt-lg-0">
                  <div
                    className="h-100 p-3 p-md-4 d-flex flex-column justify-content-center"
                    style={{
                      border: "1px solid white",
                      borderRadius: "1rem",
                      background: "rgba(112,112,112,0.4)",
                    }}
                  >
                    <h6>
                      {result
                        ? `${t("resultBox.calculatedSingle.you")} ${
                            method === "singleleggedstand"
                              ? t("resultBox.calculatedSingle.held")
                              : t("resultBox.calculatedSingle.completed")
                          } ${rawInput} ${t(
                            "resultBox.calculatedSingle.yourCat",
                          )} ${result}`
                        : t("resultBox.default")}
                    </h6>
                    <p className="p-sm">{t("resultBox.description")}</p>
                    <div
                      className="bg-white w-100 p-3 text-center mt-3"
                      style={{
                        border: "0.5px solid gray",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <p className="m-0 grey-text p-sm">
                        {t("resultBox.note")}
                      </p>
                      <Button text={t("resultBox.cta")} to="/classes" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <FitnessToolsResultCard
              type="Balance"
              result={
                result ? { result, timestamp: new Date().toISOString() } : null
              }
              historyData={historyData}
              showHistory={showHistory}
              toggleHistory={toggleHistory}
              handleSaveResult={handleSaveResult}
              handleDeleteLast={handleDeleteLast}
              formatHistoryEntry={formatHistoryEntry}
              formatDateTime={formatDateTime}
              chartData={balenceData?.data || []}
              period={period}
              handlePeriodChange={handlePeriodChange}
              selectedBalence={selectedBalence}
              handleBalenceTabChange={handleBalenceTabChange}
            />

            {(aiLoading || aiSuggestion) && (
              <div
                className={`ai-suggestion-box ${highlightAI ? "ai-highlight" : ""}`}
                ref={aiRef}
              >
                <h4 className="text-white mb-3">
                  {/* <FontAwesomeIcon icon={faRobot} color="var(--primaryColor)" />{" "} */}
                  <img
                    src="https://traineego.com/assets/images/favicon-2026.ico"
                    alt="AI Coach"
                    style={{
                      width: "45px",
                      marginRight: "8px",
                      borderRadius: "25px",
                    }}
                  />
                  {t("aiSuggestion.title")}
                </h4>

                {showSkeleton ? (
                  // <p style={{ color: "#aaa" }}>
                  //   <Spinner size="sm" /> {t("aiSuggestion.loading")}
                  // </p>
                  <AISkeleton />
                ) : (
                  <div
                    style={{ color: "#d1d1d6", lineHeight: "1.7" }}
                    dangerouslySetInnerHTML={{
                      __html: formatAISuggestion(aiSuggestion),
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <OtherFitnessTestSection
        data={FITNESSTEST.filter(
          (item) => item.route !== window.location.pathname,
        )}
      />
      <Footer />
    </>
  );
};

export default BalanceTest;
